import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MessageCircle, ChevronUp, X, Plus } from "lucide-react";
import { COMPANY, useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/ui";

export default function FloatingActions() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const actions = [
    { icon: Phone, href: `tel:${COMPANY.phoneRaw}`, label: t({ ro: "Sună", en: "Call" }), color: "bg-blue-600", tid: "fab-phone" },
    { icon: MessageCircle, href: `https://wa.me/${COMPANY.whatsapp}`, label: "WhatsApp", color: "bg-green-600", tid: "fab-whatsapp" },
    { icon: Mail, href: `mailto:${COMPANY.email}`, label: t({ ro: "E-mail", en: "Email" }), color: "bg-sky-500", tid: "fab-email" },
  ];

  return (
    <>
      {/* Scroll to top */}
      <AnimatePresence>
        {showTop && (
          <motion.button
            data-testid="scroll-top"
            onClick={() => scrollToId("acasa")}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            className="fixed bottom-6 left-5 md:left-6 z-40 grid h-12 w-12 place-items-center rounded-full glass hover:border-sky-400/50 transition-colors"
          >
            <ChevronUp className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating action stack (bottom right) */}
      <div className="fixed bottom-6 right-5 md:right-6 z-40 flex flex-col items-end gap-3">
        <AnimatePresence>
          {open && actions.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.a
                key={a.tid}
                data-testid={a.tid}
                href={a.href}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 20, scale: 0.6 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.6 }}
                transition={{ delay: i * 0.05 }}
                className="group flex items-center gap-3"
              >
                <span className="glass rounded-full px-3 py-1.5 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">{a.label}</span>
                <span className={`grid h-12 w-12 place-items-center rounded-full ${a.color} text-white shadow-lg`}>
                  <Icon className="h-5 w-5" />
                </span>
              </motion.a>
            );
          })}
        </AnimatePresence>
        <button
          data-testid="fab-toggle"
          onClick={() => setOpen((o) => !o)}
          className="grid h-14 w-14 place-items-center rounded-full bg-white text-slate-950 shadow-xl shadow-black/40 hover:bg-sky-300 transition-colors"
        >
          <motion.span animate={{ rotate: open ? 45 : 0 }}>
            {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
          </motion.span>
        </button>
      </div>
    </>
  );
}
